import React from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LayersIcon from '@mui/icons-material/Layers';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import dayjs from 'dayjs';
import { color } from '../../styles';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { ChatMessage, useChatMessages } from '../../hooks/useChatMessages';
import { selfDataSelector, showMedicalContentSelector } from '../../redux/config/selfData';
import { Dialog } from '../../components/mui/dialog';
import { CHAT_FILE_ACCEPT, isChatImageFile, isValidChatFile } from '../../lib/chatFile';
import { splitChatMessageText } from '../../lib/chatMessage';

const PageContainer = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  height: 100%;
  padding: 24px 26px 18px;
  box-sizing: border-box;
  background: ${color.bg_gray};
`;

const PanelContainer = (isFullscreen: boolean) => css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${isFullscreen ? color.bg_gray : color.white};
`;

const Header = css`
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 0 0 auto;
`;

const PanelHeader = (isMedical: boolean) => css`
  display: flex;
  align-items: center;
  min-height: 46px;
  padding: 0 10px 0 18px;
  background: ${isMedical ? color.md_chat_blue : color.green};
  color: ${color.white};
`;

const PanelTitle = css`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const PanelHeaderActions = css`
  display: flex;
  align-items: center;
  margin-left: auto;

  .MuiIconButton-root {
    color: ${color.white};
  }
`;

const Title = css`
  margin: 0;
  color: ${color.dark_gray};
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const RefreshButton = (isMedical: boolean) => css`
  min-width: auto;
  padding: 7px 16px;
  border-radius: 999px;
  background: ${color.white};
  color: ${color.dark_gray};
  box-shadow: none;
  font-size: 14px;

  &:hover {
    background: ${color.light_gray};
    box-shadow: none;
  }

  .MuiButton-startIcon {
    margin-right: 5px;
    color: ${isMedical ? color.md_chat_blue : color.green};
  }
`;

const ChatCard = (isPanel: boolean) => css`
  display: flex;
  position: relative;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: ${isPanel ? '0' : '10px'};
  background: ${color.white};
  box-shadow: ${isPanel ? 'none' : '0 1px 5px rgba(55, 70, 85, 0.08)'};
`;

const CardToolbar = (isCompactPanel: boolean) => css`
  display: flex;
  position: relative;
  align-items: center;
  min-height: ${isCompactPanel ? '48px' : '52px'};
  padding: ${isCompactPanel ? '0 12px 0 18px' : '0 18px 0 38px'};
  box-sizing: border-box;
  flex: 0 0 auto;
`;

const BookmarkToggle = (isMedical: boolean) => css`
  align-self: flex-start;
  min-width: auto;
  padding: 9px 18px;
  border: 1px solid ${color.border_gray};
  border-top: 0;
  border-radius: 0 0 10px 10px;
  color: ${color.dark_gray};
  font-weight: 600;

  &:hover {
    border-color: ${isMedical ? color.md_chat_blue : color.green};
    background: ${isMedical ? color.md_bg_light_blue : color.bg_pale_green};
  }

  img {
    width: 16px;
    height: 19px;
  }
`;

const BookmarkPanel = (isCompactPanel: boolean) => css`
  position: absolute;
  z-index: 10;
  top: 46px;
  left: ${isCompactPanel ? '18px' : '38px'};
  width: ${isCompactPanel ? 'min(430px, calc(100% - 36px))' : 'min(430px, calc(100% - 76px))'};
  padding: 14px;
  border: 1px solid ${color.border_gray_dark};
  border-radius: 10px;
  box-sizing: border-box;
  background: ${color.white};
  box-shadow: 0 8px 24px rgba(40, 55, 70, 0.16);
`;

const BookmarkPanelTitle = css`
  margin-bottom: 8px;
  color: ${color.dark_gray};
  font-size: 13px;
  font-weight: 700;
`;

const BookmarkItem = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  overflow: hidden;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${color.dark_gray};
  cursor: pointer;
  font-size: 13px;
  text-align: left;

  &:hover {
    background: ${color.bg_light_green};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > img:first-of-type {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
  }

  > img {
    flex: 0 0 auto;
  }
`;

const NoBookmarks = css`
  padding: 14px 8px;
  color: ${color.gray};
  font-size: 13px;
  text-align: center;
`;

const MessageArea = (isPanel: boolean, isFullscreen = false) => css`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: ${isFullscreen ? '18px 42px 32px' : isPanel ? '18px 36px 24px' : '26px 38px 36px'};
  background: ${color.white};

  @media (max-width: 720px) {
    padding: 22px 18px 30px;
  }
`;

const MessageItem = (isMine: boolean, isPanel = false, isFullscreen = false) => css`
  display: flex;
  flex-direction: column;
  align-items: ${isMine ? 'flex-end' : 'flex-start'};
  width: fit-content;
  max-width: ${isFullscreen ? '44%' : isPanel ? '78%' : '58%'};
  margin-right: ${isMine ? '0' : 'auto'};
  margin-bottom: 32px;
  margin-left: ${isMine ? 'auto' : '0'};

  @media (max-width: 720px) {
    max-width: 86%;
  }
`;

const Bubble = (isMine: boolean, isPanel = false, isFullscreen = false, isMedical = false) => {
  const bubbleColor = isMine ? (isMedical ? color.md_bg_light_blue : color.bg_light_green) : '#f2f2f2';
  return css`
    position: relative;
    min-width: ${isPanel && !isFullscreen ? '150px' : '180px'};
    max-width: 100%;
    padding: ${isPanel && !isFullscreen ? '14px 38px 32px 14px' : '18px 46px 38px 20px'};
    border-radius: 10px;
    box-sizing: border-box;
    background: ${bubbleColor};
    color: ${color.dark_gray};
    font-size: 14px;
    line-height: 1.65;
    overflow-wrap: anywhere;
    white-space: pre-wrap;

    &::after {
      position: absolute;
      top: 54px;
      ${isMine ? 'right' : 'left'}: -12px;
      width: 0;
      height: 0;
      border-top: 9px solid transparent;
      border-bottom: 9px solid transparent;
      border-${isMine ? 'left' : 'right'}: 14px solid ${bubbleColor};
      content: '';
    }
  `;
};

const InquiryPromptBubble = css`
  padding: 18px 20px;

  &::after {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const SenderName = css`
  display: block;
  margin-bottom: 2px;
  font-size: 14px;
  font-weight: 700;
`;

const DeleteButton = css`
  position: absolute;
  top: 5px;
  right: 5px;
  color: ${color.dark_gray};
`;

const PinButton = css`
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 25px;
  height: 25px;
  padding: 0;

  &:hover {
    background: transparent;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const AttachmentButton = css`
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
  margin-top: 8px;
  padding: 6px 10px;
  overflow: hidden;
  border: 1px solid ${color.border_gray_dark};
  border-radius: 4px;
  background: ${color.white};
  color: ${color.dark_gray};
  cursor: pointer;
  font-size: 14px;
  text-align: left;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  img {
    flex: 0 0 auto;
  }
`;

const MessageFooter = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  width: 100%;
  margin-top: 4px;
  color: ${color.dark_gray};
  font-size: 12px;
`;

const ReadMeta = css`
  margin-right: auto;
  white-space: nowrap;
`;

const LoadingWrapper = css`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const EmptyMessage = css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: ${color.gray};
`;

const LoadMoreArea = css`
  display: flex;
  justify-content: center;
  margin-bottom: 26px;
`;

const Composer = (isPanel: boolean, isFullscreen = false) => css`
  display: flex;
  flex: 0 0 auto;
  flex-direction: ${isPanel && !isFullscreen ? 'row' : 'column'};
  align-items: ${isPanel && !isFullscreen ? 'center' : 'stretch'};
  gap: ${isPanel && !isFullscreen ? '8px' : '12px'};
  min-height: ${isPanel && !isFullscreen ? '72px' : '148px'};
  margin: ${isFullscreen ? '20px 60px' : '0'};
  padding: ${isPanel && !isFullscreen ? '10px 14px' : '18px 20px 14px'};
  border-top: ${isPanel && !isFullscreen ? `1px solid ${color.border_gray}` : '0'};
  border-radius: ${isPanel && !isFullscreen ? '0' : '10px'};
  box-sizing: border-box;
  background: ${color.white};
  box-shadow: ${isPanel && !isFullscreen ? 'none' : '0 1px 5px rgba(55, 70, 85, 0.08)'};

  @media (max-width: 720px) {
    margin: ${isFullscreen ? '12px' : '0'};
  }
`;

const MessageInput = css`
  flex: 1 1 auto;
  min-width: 0;

  .MuiOutlinedInput-root {
    align-items: flex-start;
    padding: 0;
    color: ${color.dark_gray};
    font-size: 14px;
    line-height: 1.65;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 0;
  }

  textarea {
    min-height: 52px;
  }
`;

const PanelUploadButton = css`
  flex-direction: column;
  gap: 2px 0;
  min-width: 52px;
  padding: 6px 0;
  color: ${color.dark_gray};
  font-size: 8px;

  .MuiButton-startIcon {
    margin: 0;
  }
`;

const ComposerBottom = css`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`;

const AttachmentPreview = css`
  display: flex;
  position: relative;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 42%;
  padding: 7px 32px 7px 10px;
  border: 1px solid ${color.border_gray_dark};
  border-radius: 5px;
  color: ${color.dark_gray};
  font-size: 13px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > img {
    flex: 0 0 auto;
  }
`;

const RemoveAttachmentButton = css`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 20px;
  height: 20px;
  background: ${color.dark_gray};
  color: ${color.white};

  &:hover {
    background: ${color.black};
  }

  svg {
    font-size: 13px;
  }
`;

const HiddenFileInput = css`
  display: none;
`;

const UploadButton = css`
  min-width: auto;
  margin-left: auto;
  padding: 8px 10px;
  color: ${color.dark_gray};
  font-weight: 700;
  white-space: nowrap;

  .MuiButton-startIcon {
    margin-right: 7px;
  }
`;

const SendButton = css`
  min-width: 92px;
  padding: 10px 20px;
  border-radius: 7px;
  background: ${color.green};
  color: ${color.white};
  font-weight: 700;
  box-shadow: none;

  &:hover {
    background: #07999b;
    box-shadow: none;
  }

  &:disabled {
    background: ${color.input_gray};
    color: ${color.white};
  }
`;

interface ChatDialogProps {
  isOpen: boolean;
  title: React.ReactNode;
  children?: React.ReactNode;
  okText?: string;
  showCancelButton?: boolean;
  onClose: () => void;
  onOkClick: () => void;
}

const ChatDialogTitle = css`
  display: block;
  font-size: 20px;
  font-weight: bold;
`;

const ChatDialogContent = css`
  display: flex;
  flex-direction: column;
  color: ${color.dark_gray};
  font-size: 16px;
  text-align: center;
`;

const ChatDialog = ({
  isOpen,
  title,
  children,
  okText,
  showCancelButton = false,
  onClose,
  onOkClick,
}: ChatDialogProps) => (
  <Dialog
    isOpen={isOpen}
    title={<span css={ChatDialogTitle}>{title}</span>}
    okText={okText}
    showCancelButton={showCancelButton}
    onClose={onClose}
    onOkClick={onOkClick}
  >
    {children ? <div css={ChatDialogContent}>{children}</div> : null}
  </Dialog>
);

const MessageBody = ({ body }: { body: string }) => (
  <div>
    {splitChatMessageText(body).map((part, index) =>
      part.isLink ? (
        <a
          key={`${index}-${part.text}`}
          href={part.text}
          target='_blank'
          rel='noopener noreferrer'
        >
          {part.text}
        </a>
      ) : (
        <React.Fragment key={`${index}-${part.text}`}>{part.text}</React.Fragment>
      ),
    )}
  </div>
);

interface BookmarkButtonProps {
  isPinned: boolean;
  isMedical: boolean;
  disabled: boolean;
  onClick: () => void;
}

const BookmarkButton = ({ isPinned, isMedical, disabled, onClick }: BookmarkButtonProps) => (
  <IconButton css={PinButton} disabled={disabled} onClick={onClick}>
    <img
      src={
        isPinned
          ? isMedical
            ? '/images/chat/bookmark_enable_med.svg'
            : '/images/chat/bookmark_enable.svg'
          : '/images/chat/bookmark_disable.svg'
      }
      alt='bookmark'
    />
  </IconButton>
);

const HISTORY_LOAD_SCROLL_THRESHOLD_PX = 20;

interface ChatViewProps {
  variant: 'page' | 'panel';
  isDocked?: boolean;
  isFullscreen?: boolean;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  onToggleLayout?: () => void;
}

const ChatView = ({
  variant,
  isDocked = true,
  isFullscreen = false,
  onClose,
  onToggleFullscreen,
  onToggleLayout,
}: ChatViewProps) => {
  const isPanel = variant === 'panel';
  const { selfUser } = useSelector(selfDataSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { t, i18n } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const {
    messages,
    isLoading,
    isLoadingMore,
    isSending,
    isDeletingMessage,
    isUpdatingPin,
    pinnedMessages,
    error,
    hasMore,
    fetchMessages,
    sendMessage,
    loadMore,
    downloadAttachment,
    togglePin,
    deleteMessage,
  } = useChatMessages(selfUser.nursingHomeId, selfUser.userName ?? '');
  const [input, setInput] = React.useState('');
  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [showBookmarks, setShowBookmarks] = React.useState(false);
  const [isUploadRestrictionModalOpen, setIsUploadRestrictionModalOpen] = React.useState(false);
  const [isPinLimitDialogOpen, setIsPinLimitDialogOpen] = React.useState(false);
  const [isBookmarkedMessageDeleteDialogOpen, setIsBookmarkedMessageDeleteDialogOpen] = React.useState(false);
  const [deleteTargetMessage, setDeleteTargetMessage] = React.useState<ChatMessage | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [jumpTargetMessageUuid, setJumpTargetMessageUuid] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messageAreaRef = React.useRef<HTMLDivElement>(null);
  const scrollPositionBeforeHistoryLoadRef = React.useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const oldestMessageId = messages[0]?.messageUuid;
  const newestMessageId = messages[messages.length - 1]?.messageUuid;

  const handleLoadMore = React.useCallback(async () => {
    const messageArea = messageAreaRef.current;
    if (!messageArea || !hasMore || isLoadingMore || scrollPositionBeforeHistoryLoadRef.current) return;

    scrollPositionBeforeHistoryLoadRef.current = {
      scrollHeight: messageArea.scrollHeight,
      scrollTop: messageArea.scrollTop,
    };

    try {
      await loadMore();
    } catch (err) {
      scrollPositionBeforeHistoryLoadRef.current = null;
      throw err;
    }
  }, [hasMore, isLoadingMore, loadMore]);

  const handleMessageAreaScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (scrollPositionBeforeHistoryLoadRef.current) return;

      const messageArea = event.currentTarget;
      if (messageArea.scrollHeight <= messageArea.clientHeight) return;

      if (messageArea.scrollTop <= HISTORY_LOAD_SCROLL_THRESHOLD_PX) {
        void handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  const handleSend = async () => {
    if ((!input.trim() && !attachment) || isSending) return;
    try {
      await sendMessage(input, attachment);
      setInput('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      showSnackbar(t('chatSendError', 'メッセージの送信に失敗しました'), 'error');
    }
  };

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setAttachment(null);
      return;
    }
    if (!isValidChatFile(file)) {
      setAttachment(null);
      event.target.value = '';
      setIsUploadRestrictionModalOpen(true);
      return;
    }
    setAttachment(file);
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchMessages('refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDownload = async (fileName: string, realFileName: string) => {
    const previewWindow = window.open('', '_blank');
    try {
      const blob = await downloadAttachment(fileName);
      const extension = realFileName.split('.').pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        txt: 'text/plain',
      };
      const typedBlob = new Blob([blob], {
        type: (extension && mimeTypes[extension]) || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(typedBlob);
      if (previewWindow) {
        previewWindow.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      previewWindow?.close();
      showSnackbar(t('chatDownloadError', '添付ファイルの取得に失敗しました'), 'error');
    }
  };

  const handleDelete = (message: ChatMessage) => {
    if (message.isPinned) {
      setIsBookmarkedMessageDeleteDialogOpen(true);
      return;
    }

    setDeleteTargetMessage(message);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetMessage) return;

    const targetMessage = deleteTargetMessage;
    setDeleteTargetMessage(null);

    try {
      await deleteMessage(targetMessage);
      showSnackbar(t('messageDeleted', 'メッセージを削除しました'), 'success');
    } catch {
      showSnackbar(t('failedToDeleteTheMessage', 'メッセージの削除に失敗しました'), 'error');
    }
  };

  const handlePin = async (message: ChatMessage) => {
    try {
      const result = await togglePin(message);
      if (result === 'limitReached') {
        setIsPinLimitDialogOpen(true);
      }
    } catch {
      showSnackbar(t('failedToUpdateThePin', 'ピン留めの更新に失敗しました'), 'error');
    }
  };

  const handlePinnedMessageClick = (messageUuid: string) => {
    setShowBookmarks(false);
    const element = document.getElementById(`message-${messageUuid}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setJumpTargetMessageUuid(messageUuid);
  };

  React.useEffect(() => {
    if (!jumpTargetMessageUuid || isLoading || isLoadingMore) return;

    const element = document.getElementById(`message-${jumpTargetMessageUuid}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setJumpTargetMessageUuid(null);
      return;
    }
    if (hasMore) {
      void handleLoadMore();
      return;
    }

    showSnackbar(t('targetMessageCouldNotBeFound', '対象のメッセージが見つかりません'), 'error');
    setJumpTargetMessageUuid(null);
  }, [handleLoadMore, hasMore, isLoading, isLoadingMore, jumpTargetMessageUuid, showSnackbar, t]);

  React.useLayoutEffect(() => {
    const previousPosition = scrollPositionBeforeHistoryLoadRef.current;
    const messageArea = messageAreaRef.current;
    if (!previousPosition || !messageArea || isLoadingMore) return;

    const addedContentHeight = messageArea.scrollHeight - previousPosition.scrollHeight;
    messageArea.scrollTop = previousPosition.scrollTop + addedContentHeight;
    scrollPositionBeforeHistoryLoadRef.current = null;
  }, [isLoadingMore, oldestMessageId]);

  React.useLayoutEffect(() => {
    if (isLoading || !newestMessageId || scrollPositionBeforeHistoryLoadRef.current) return;

    const messageArea = messageAreaRef.current;
    if (messageArea) {
      messageArea.scrollTop = messageArea.scrollHeight;
    }
  }, [isLoading, newestMessageId]);

  return (
    <div css={isPanel ? PanelContainer(isFullscreen) : PageContainer}>
      {isPanel ? (
        <header css={PanelHeader(showMedicalContent)}>
          <h2 css={PanelTitle}>{t('chatRoom', 'チャットルーム')}</h2>
          <div css={PanelHeaderActions}>
            {!isFullscreen && (
              <IconButton onClick={onToggleLayout}>{isDocked ? <LayersIcon /> : <VerticalSplitIcon />}</IconButton>
            )}
            <IconButton onClick={onToggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </header>
      ) : (
        <header css={Header}>
          <h1 css={Title}>{t('chatRoom', 'チャットルーム')}</h1>
          <Button
            css={RefreshButton(showMedicalContent)}
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {t('update', '更新')}
          </Button>
        </header>
      )}

      <div css={ChatCard(isPanel)}>
        <div css={CardToolbar(isPanel && !isFullscreen)}>
          <Button
            css={BookmarkToggle(showMedicalContent)}
            startIcon={<img src='/images/chat/bookmark_pin.svg' alt='' />}
            aria-expanded={showBookmarks}
            onClick={() => setShowBookmarks((current) => !current)}
          >
            {showBookmarks
              ? t('hideBookmarks', 'ブックマークを閉じる')
              : t('displayBookmarks', 'ブックマーク表示')}
          </Button>
          {showBookmarks && (
            <div css={BookmarkPanel(isPanel && !isFullscreen)}>
              <div css={BookmarkPanelTitle}>
                {t('upToThreePinsCanBeSharedWithinEachFacility', '1施設共有で3つまで登録可能です')}
              </div>
              {pinnedMessages.length === 0 ? (
                <div css={NoBookmarks}>{t('noBookmarkedMessages', 'ブックマークされたメッセージはありません')}</div>
              ) : (
                pinnedMessages.map((message) => (
                  <button
                    key={message.messageUuid}
                    type='button'
                    css={BookmarkItem}
                    onClick={() => handlePinnedMessageClick(message.messageUuid)}
                  >
                    <img
                      src={
                        showMedicalContent
                          ? '/images/chat/bookmark_enable_med.svg'
                          : '/images/chat/bookmark_enable.svg'
                      }
                      alt=''
                    />
                    {message.realFileName && (
                      <img
                        src={
                          isChatImageFile(message.realFileName)
                            ? '/images/chat/icon_pic.svg'
                            : '/images/chat/icon_doc.svg'
                        }
                        alt=''
                      />
                    )}
                    <span>{message.preview}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div ref={messageAreaRef} css={MessageArea(isPanel, isFullscreen)} onScroll={handleMessageAreaScroll}>
          {isLoadingMore && (
            <div css={LoadMoreArea}>
              <CircularProgress size={20} />
            </div>
          )}
          {isLoading ? (
            <div css={LoadingWrapper}>
              <CircularProgress size={28} />
            </div>
          ) : error ? (
            <div css={EmptyMessage}>{t('chatLoadError', 'メッセージの取得に失敗しました')}</div>
          ) : (
            <>
              {!hasMore && (
                <div css={MessageItem(false, isPanel, isFullscreen)}>
                  <div css={[Bubble(false, isPanel, isFullscreen, showMedicalContent), InquiryPromptBubble]}>
                    {t('whatIsYourInquiry', 'お問い合わせ内容を教えてください。')}
                  </div>
                </div>
              )}
              {messages.map((message) => {
                const isMine = !message.isAdmin;
                return (
                  <div
                    id={`message-${message.messageUuid}`}
                    key={message.messageUuid}
                    css={MessageItem(isMine, isPanel, isFullscreen)}
                  >
                    <div css={Bubble(isMine, isPanel, isFullscreen, showMedicalContent)}>
                      <span css={SenderName}>{message.senderUserName}</span>
                      {message.body && <MessageBody body={message.body} />}
                      {message.fileName && message.realFileName && (
                        <button
                          type='button'
                          css={AttachmentButton}
                          onClick={() => handleDownload(message.fileName as string, message.realFileName as string)}
                        >
                          <img
                            src={
                              isChatImageFile(message.realFileName)
                                ? '/images/chat/icon_pic.svg'
                                : '/images/chat/icon_doc.svg'
                            }
                            alt=''
                          />
                          <span>{message.realFileName}</span>
                        </button>
                      )}
                      {isMine && (
                        <IconButton
                          size='small'
                          css={DeleteButton}
                          disabled={isDeletingMessage}
                          onClick={() => handleDelete(message)}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      )}
                      <BookmarkButton
                        isPinned={message.isPinned}
                        isMedical={showMedicalContent}
                        disabled={isUpdatingPin}
                        onClick={() => handlePin(message)}
                      />
                    </div>
                    <div css={MessageFooter}>
                      {isMine && message.readCount > 0 && (
                        <span css={ReadMeta}>
                          {t('readCount', '既読')}: {message.readCount}
                        </span>
                      )}
                      <time>{dayjs(message.createdAt).format('YYYY/MM/DD HH:mm')}</time>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <div css={Composer(isPanel, isFullscreen)}>
        {isPanel && !isFullscreen && (
          <>
            <input
              ref={fileInputRef}
              css={HiddenFileInput}
              type='file'
              accept={CHAT_FILE_ACCEPT}
              disabled={isSending}
              onChange={handleAttachmentChange}
            />
            <Button
              css={PanelUploadButton}
              startIcon={<UploadFileIcon />}
              disabled={isSending}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('upload', 'アップロード')}
            </Button>
          </>
        )}
        <TextField
          css={MessageInput}
          fullWidth
          multiline={!isPanel || isFullscreen}
          maxRows={isPanel && !isFullscreen ? 1 : 5}
          placeholder={t('enterYourMessage', 'メッセージを入力')}
          value={input}
          onChange={(event) => setInput(event.target.value)}
        />
        {(!isPanel || isFullscreen) && (
          <div css={ComposerBottom}>
            {attachment && (
              <div css={AttachmentPreview}>
                <img
                  src={isChatImageFile(attachment.name) ? '/images/chat/icon_pic.svg' : '/images/chat/icon_doc.svg'}
                  alt=''
                />
                <span>{attachment.name}</span>
                <IconButton
                  css={RemoveAttachmentButton}
                  onClick={() => {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            )}
            <input
              ref={fileInputRef}
              css={HiddenFileInput}
              type='file'
              accept={CHAT_FILE_ACCEPT}
              disabled={isSending}
              onChange={handleAttachmentChange}
            />
            <Button
              css={UploadButton}
              startIcon={<UploadFileIcon />}
              disabled={isSending}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('fileUpload', 'ファイルアップロード')}
            </Button>
            <Button
              css={SendButton}
              variant='contained'
              disabled={(!input.trim() && !attachment) || isSending}
              onClick={handleSend}
            >
              {isSending ? <CircularProgress size={20} color='inherit' /> : t('send', '送信')}
            </Button>
          </div>
        )}
        {isPanel && !isFullscreen && attachment && (
          <div css={AttachmentPreview}>
            <img
              src={isChatImageFile(attachment.name) ? '/images/chat/icon_pic.svg' : '/images/chat/icon_doc.svg'}
              alt=''
            />
            <span>{attachment.name}</span>
            <IconButton
              css={RemoveAttachmentButton}
              onClick={() => {
                setAttachment(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}
        {isPanel && !isFullscreen && (
          <Button
            css={SendButton}
            variant='contained'
            disabled={(!input.trim() && !attachment) || isSending}
            onClick={handleSend}
          >
            {isSending ? <CircularProgress size={20} color='inherit' /> : t('send', '送信')}
          </Button>
        )}
      </div>
      <ChatDialog
        isOpen={isUploadRestrictionModalOpen}
        title={t('thisFileCannotBeUploaded', 'このファイルはアップロードできません')}
        onClose={() => setIsUploadRestrictionModalOpen(false)}
        onOkClick={() => setIsUploadRestrictionModalOpen(false)}
      >
        <>
          <span>
            {i18n.language.startsWith('ja') ? '※' : '*'}
            {t('fileSize5MbOrLess', 'ファイルサイズ 5MB以下')}
          </span>
          <span>
            {i18n.language.startsWith('ja') ? '※' : '*'}
            {t('pngJpegTxtPdfOnly', 'png・jpeg・txt・pdfのみ')}
          </span>
        </>
      </ChatDialog>
      <ChatDialog
        isOpen={isPinLimitDialogOpen}
        title={t('upTo3MessagesCanBeBookmarked', 'ブックマークできるメッセージは3つまでです')}
        onClose={() => setIsPinLimitDialogOpen(false)}
        onOkClick={() => setIsPinLimitDialogOpen(false)}
      >
        {t(
          'ifYouWantToBookmarkThisMessagePleaseRemoveOtherBookmarks',
          'こちらのメッセージをブックマークする場合は、他のブックマークを外してください',
        )}
      </ChatDialog>
      <ChatDialog
        isOpen={deleteTargetMessage !== null}
        title={t('doYouWantToDeleteTheMessage', 'メッセージを削除しますか？')}
        okText={t('delete', '削除')}
        showCancelButton
        onClose={() => setDeleteTargetMessage(null)}
        onOkClick={handleDeleteConfirm}
      />
      <ChatDialog
        isOpen={isBookmarkedMessageDeleteDialogOpen}
        title={t('bookmarkedMessagesCannotBeDeleted', 'ブックマークしたメッセージは削除できません')}
        onClose={() => setIsBookmarkedMessageDeleteDialogOpen(false)}
        onOkClick={() => setIsBookmarkedMessageDeleteDialogOpen(false)}
      />
    </div>
  );
};

export const Chat: React.FC = () => <ChatView variant='page' />;

interface ChatPanelProps {
  isDocked: boolean;
  isFullscreen: boolean;
  onClose: () => void;
  onToggleFullscreen: () => void;
  onToggleLayout: () => void;
}

export const ChatPanel = ({ isDocked, isFullscreen, onClose, onToggleFullscreen, onToggleLayout }: ChatPanelProps) => (
  <ChatView
    variant='panel'
    isDocked={isDocked}
    isFullscreen={isFullscreen}
    onClose={onClose}
    onToggleFullscreen={onToggleFullscreen}
    onToggleLayout={onToggleLayout}
  />
);
